import { IResourceComponentsProps, useList, useNavigation, usePermissions, useShow } from "@pankod/refine-core";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list';
import { IWorkPlan } from "interfaces/work-plan.interface";
import { Button, Typography, useModal, List } from "@pankod/refine-antd";
import { useEffect, useRef, useState } from "react";
import { PermissionsConstants } from "constants/permissions";
import { useLocation } from "@pankod/refine-react-router-v6";
import { statusOptions } from "../../constants/workPlan.constants";
import { impactAxes } from "../../constants/workPlan.constants";
import { impactAxisIcons } from "../../constants/impactAxisIcons";
import { TableOutlined } from "@ant-design/icons";
import { WorkPlanDetailModal } from "components/molecules/work-plan-detail-modal";

const { Title, Text } = Typography;

const statusColors: { [key: string]: string } = {
  "SUBMITTED": "#1890ff",      // Azul
  "OBSERVATION": "#faad14",    // Amarillo/Naranja
  "RESOLVED": "#52c41a",       // Verde
  "AUTHORIZED": "#722ed1",     // Púrpura
  "FINISHED": "#52c41a",       // Verde
};

const impactAxisColors: { [key: number]: string } = {
  1: "#87CEEB",      // Azul celeste
  2: "#9932CC",    // Morado
  3: "#FFA500", // Naranja
  4: "#20B2AA",         // Verde azulado/turquesa
  5: "#2F4F4F",                   // Gris oscuro/antracita
  6: "#228B22",           // Verde institucional
};

export const WorkPlanCalendarPage: React.FC<IResourceComponentsProps> = () => {

  const { create, push } = useNavigation();
  const { data: permissionsData } = usePermissions();

  const location: any = useLocation();
  const calendarRef = useRef<FullCalendar | null>(null);

  const [initialView, setInitialView] = useState<string>("dayGridMonth");

  const { data, isLoading, refetch } = useList<IWorkPlan>({
    resource: "work-plans",
    config: {
      hasPagination: false
    }
  });

  const { modalProps: showModalProps, show, close } = useModal();
  const { queryResult, showId, setShowId } = useShow<IWorkPlan>({
    resource: "work-plans"
  });
  const { data: showData } = queryResult;
  const showRecord = showData?.data;

  const workPlanEvents = data?.data.map((workPlan: any) => {
    let start = workPlan.start_date;
    let end = workPlan.end_date;
    
    // Si hay tiempos específicos, los agregamos a las fechas
    if (workPlan.start_time) {
      start = `${workPlan.start_date}T${workPlan.start_time}`;
    }
    
    if (workPlan.end_date && workPlan.end_time) {
      end = `${workPlan.end_date}T${workPlan.end_time}`;
    } else if (workPlan.end_date) {
      // Si solo hay fecha final sin hora, agregamos un día para mostrar correctamente
      end = new Date(new Date(workPlan.end_date).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    }

    return {
      id: workPlan.id,
      title: workPlan.strategy || `Plan ${workPlan.id}`,
      start, 
      end,
      extendedProps: {
        work_plan_position: workPlan.work_plan_position,
        impact_axis: workPlan.impact_axis,
        status: workPlan.status,
        total_budget: workPlan.total_budget,
        strategy: workPlan.strategy,
        audience: workPlan.audience,
        scope: workPlan.scope,
        start_time: workPlan.start_time,
        end_time: workPlan.end_time,
      },
      backgroundColor: impactAxisColors[workPlan.impact_axis] || '#1890ff',
      borderColor: impactAxisColors[workPlan.impact_axis] || '#1890ff',
    };
  }) || [];

  useEffect(() => {
    if (location.state?.defaultCalendarView) {
      setInitialView(location.state?.defaultCalendarView);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.defaultCalendarView && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(location.state?.defaultCalendarView);
    }
  }, [location.state]);

  const handleEventClick = (info: any) => {
        console.log(info.event.id);
    setShowId(info.event.id);
    show();
  };

  const handleCloseModal = () => {
    close();
    refetch();
  };

  const renderEventContent = (eventInfo: any) => {
    const { extendedProps } = eventInfo.event;

    const normalizedTimeText = eventInfo.timeText
      ? eventInfo.timeText.includes(":")
        ? eventInfo.timeText
        : `${eventInfo.timeText}:00`
      : "";

    const impactAxisIcon = impactAxisIcons[extendedProps.impact_axis] || "📋";

    return (
      <div className="calendar-custom-event">
        <div className="calendar-event-details">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '14px' }}>{impactAxisIcon}</span>
            <b>{normalizedTimeText || (extendedProps.start_time || "")}</b>
            <span>{extendedProps.work_plan_position?.name || "--"}</span>
          </div>
          <span className="calendar-event-status">
            {statusOptions[extendedProps.status] || extendedProps.status}
          </span>
        </div>
        <div className="calendar-event-details">
          <span style={{ fontSize: '10px', fontWeight: 'bold' }}>
            {impactAxes.find((axis: { id: number; name: string }) => axis.id == extendedProps.impact_axis)?.name || extendedProps.impact_axis}
          </span>
        </div>
        <i className="calendar-event-title">{eventInfo.event.title}</i>
        {extendedProps.total_budget && (
          <div className="calendar-event-budget">
            ${Number(extendedProps.total_budget).toLocaleString()}
          </div>
        )}
      </div>
    );
  }

  const hasCreateWorkPlanPermission = true; // Ajustar según los permisos correspondientes

  const customButtons = hasCreateWorkPlanPermission ? {
    newWorkPlan: {
      text: 'Nuevo Plan',
      click: function() {
        create('work-plans');
      }
    }
  } : undefined;

  return (
    <List
      title="Calendario de Plan de Trabajo"
      headerButtons={({ defaultButtons }) => ([
        ...(Array.isArray(defaultButtons) ? defaultButtons : [defaultButtons]),
        <Button 
          key="listView" 
          type="default" 
          icon={<TableOutlined />}
          onClick={() => push('/work-plans')}
        >
          Vista Lista
        </Button>
      ])}
    >
      {isLoading ? (
        <p>Cargando...</p>
      ) : (
        <div className="calendar-wrapper">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            initialView={initialView}
            weekends={true}
            events={workPlanEvents}
            eventContent={renderEventContent}
            eventClick={handleEventClick}
            locale={'es'}
            customButtons={customButtons}
            headerToolbar={{
              left: hasCreateWorkPlanPermission ? 'prev,next today newWorkPlan' : 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay listWeek,listMonth'
            }}
            buttonText={{
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              listWeek: 'Lista Semanal',
              listMonth: 'Lista Mensual',
            }}
            themeSystem="bootstrap5"
          />
        </div>
      )}

      <WorkPlanDetailModal
        visible={!!showModalProps.visible}
        onCancel={handleCloseModal}
        onOk={handleCloseModal}
        isLoading={isLoading}
        showId={showId || ""}
        showRecord={showRecord}
        hasEditPermission={hasCreateWorkPlanPermission}
      />
    </List>
  );
};
